import {useCallback, useEffect, useState, useRef, useMemo} from 'react';
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import get from 'lodash/get'
import {StatusIndicator, Button, BUTTON_VARIANTS} from 'cdk-radial'
import * as StyledComponents from '../StyledComponents'
import {GridApi, ColumnApi, ExcelStyle} from 'ag-grid-community';
import {LicenseManager} from 'ag-grid-enterprise';
import {AG_GRID_LICENSE} from '../../../../constants';
import CampaginListHeader from './CampaginListHeader';
import {useGetAllCampaigns} from '../CampaignStore/DataQueries/useCampaignsQueries';
import {AdminTableBodySkeleton} from '../../Blocks/AdminTableBodySkeleton';
import moment from 'moment';
import {useUnifyAdminStore} from '../../AdminStore';
import {useAllApplicationsStore, useAllWorkflowsStore} from '../../../../Store/index';
import { useTranslation } from 'react-i18next';

LicenseManager.setLicenseKey(
    AG_GRID_LICENSE);

// DefaultColDef sets props common to all Columns
const defaultColDef = {
    enablePivot: true,
    sortable: true,
    filter: true,
    flex: 1,
    autoHeight: true,
    minWidth: 100,
    resizable: true,
    refreshCells: true,
};

enum StatusTypes {
    POSITIVE = 'positive',
    WARNING = 'warning',
}

const CustomStatusRenderer = ({isActive}: any): JSX.Element => {
    const variant = isActive ? StatusTypes.POSITIVE : StatusTypes.WARNING;
    return (
        <StatusIndicator
            label={isActive ? 'Active' : 'Inactive'}
            variant={variant}
            showIcon
        />
    );
};

const CustomPreviewRenderer = (): JSX.Element => {
    const { t } = useTranslation();
    const setIsPreviewCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsPreviewCampaignDialogOpen);
    const onPreviewClick = (event: React.MouseEvent, params: { api: GridApi; columnApi: ColumnApi }) => {
        setIsPreviewCampaignDialogOpen(true);
    };

    return (
        <Button text={t('campaigns.preview')} variant={BUTTON_VARIANTS.TEXT} onClick={onPreviewClick}/>
    );
};

function dateValueFormatter(params: any) {
    return moment(
        params.value,
        'dddd, MMMM DD, YYYY at h:mm:ss A Z'
    ).format('MM/DD/YYYY');
};

// HELPER FOR DATE COMPARISON
function dateToNum(date: any) {
    if (date === undefined || date === null) {
        return null;
    }

    const dateObj = new Date(moment(
        date,
        'dddd, MMMM DD, YYYY at h:mm:ss A Z'
    ).format('MM/DD/YYYY'));

    const yearNumber = dateObj.getFullYear();
    const monthNumber = dateObj.getMonth();
    const dayNumber = dateObj.getDate();

    var result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
    // 22/05/2023 => 20230522
    return result;
}

// DATE COMPARATOR FOR SORTING
function dateComparator(date1: any, date2: any) {
    var date1Number = dateToNum(date1);
    var date2Number = dateToNum(date2);

    if (date1Number === null && date2Number === null) {
        return 0;
    }
    if (date1Number === null) {
        return -1;
    }
    if (date2Number === null) {
        return 1;
    }

    return date1Number - date2Number;
}



export default function CampaginList(): JSX.Element {
    const { t } = useTranslation();
    /**
     * Data store
     * */
    const setCampaignItem = useUnifyAdminStore((state) => state.setCampaignItem);
    const setIsCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsCreateCampaignDialogOpen);
    const campaignSearchValue = useUnifyAdminStore((state) => state.campaignSearchValue);
    const applications = useAllApplicationsStore((state:any) => state.applications);
    const workflows = useAllWorkflowsStore((state:any) => state.workflows);
    const archiveSwitch = useUnifyAdminStore((state) => state.campaignArchiveSwitch);
    const setCampaignList = useUnifyAdminStore((state) => state.setCampaignList);

    const columnDefinition = useMemo(() => {
    const baseColumns = [
        {
            field: 'name.enUS',
            headerName: t('campaigns.colCampaignName'),
            colId: 'name',
            width: 200,
            maxWidth: 300,
            minWidth: 200,
            cellClass: 'formattedColumn',
        },
        {
            field: 'description.enUS',
            headerName: t('campaigns.colDescription'),
            colId: 'description',
            width: 250,
            minWidth: 200,
            sortable: true,
            cellClass: 'formattedColumn'
        },
        {
            field: 'createdBy',
            headerName: t('campaigns.colCreatedBy'),
            colId: 'createdBy',
            width: 50,
            maxWidth: 250,
            minWidth: 200,
            sortable: true,
            cellClass: 'formattedColumn'
        },
        {
            cellRenderer: (params: any) => {
                return moment(
                    params.data.createdOn,
                    'dddd, MMMM DD, YYYY at h:mm:ss A Z'
                ).format('MM/DD/YYYY')
            },
            field: 'createdOn',
            headerName: t('campaigns.colCreatedDate'),
            colId: 'createdOn',
            width: 50,
            maxWidth: 180,
            minWidth: 110,
            sortable: true,
            cellClass: 'formattedColumn',
            getQuickFilterText: () => '',
            comparator: dateComparator,
            filterParams: {
                valueFormatter: dateValueFormatter,
            },
        },
        {
            field: 'isActive',
            colId: 'Status',
            cellRenderer: (params: any) => (
                <CustomStatusRenderer isActive={get(params, 'data.isActive', false)} />
            ),
            headerName: t('campaigns.colStatus'),
            width: 20,
            maxWidth: 180,
            minWidth: 110,
            sortable: true,
            getQuickFilterText: () => '',
        },
        {
            colId: 'Preview',
            cellRenderer: () => <CustomPreviewRenderer />,
            headerName: t('campaigns.colPreview'),
            minWidth: 100,
            width: 20,
            maxWidth: 180,
            sortable: true,
            getQuickFilterText: () => '',
        }
    ];

    if (archiveSwitch) {
        return baseColumns.filter(
            (col) => col.colId !== 'Status' && col.colId !== 'Preview'
        );
    }

    return baseColumns;
}, [archiveSwitch,t]);


    /**
     * Grid
     */
    const [gridApi, setGridApi] = useState<GridApi>();
    const gridRef = useRef<AgGridReact>(null);

    const onGridReady = (params: { api: GridApi; columnApi: ColumnApi }) => {
        setGridApi(params.api);
    };
    const {
        data: listOfCampaigns,
        isFetching: isCampaignsFetching,
    } = useGetAllCampaigns(archiveSwitch);


    useEffect(() => {
        if(listOfCampaigns){
            setCampaignList(listOfCampaigns);
        }
    }, [listOfCampaigns, setCampaignList]);

    const onFirstDataRendered = useCallback((params: any) => {
        // @ts-ignore
        gridApi?.current?.api?.sizeColumnsToFit();
    }, [gridApi]);

    const cellClickedListener = useCallback((event: any) => {

        event.data.targetApplications = applications && applications[0]
            .filter((app: any) => event.data.target?.applications?.includes(app.appId))
            .map((app: any) => ({ id: app.appId, label: app.name, value: app.appId }));

        if (workflows && workflows[0]) {
            event.data.targetWorkflows = workflows[0]
                .filter((workflow: any) =>
                    event.data.target?.workflows?.includes(workflow.workflowId)
                )
                .map((workflow: any) => ({
                    id: workflow.workflowId,
                    label: workflow.name,
                    value: workflow.workflowId,
                }));
        }

        event.data.targetEnterprises = event.data.target?.enterpriseIds?.join(',') || '';
        event.data.uploadedEnterprises = event.data.target?.enterpriseIds?.join(',') || '';
        const formattedArray: any =  event.data.target?.enterpriseIds?.map((name: any) => ({
            isSelected: true,
            name: name
        })) || [];
        event.data.gridEnterpriseData = formattedArray;

        event.data.targetStores = event.data.target?.stores?.join(',') || '';
        event.data.uploadedStores = event.data.target?.stores?.join(',') || '';
        const formattedStoresArray: any = event.data.target?.stores?.map((name: string) => ({
            isSelected: true,
            name: name
        })) || [];
        event.data.gridStoresData = formattedStoresArray;

        setCampaignItem(event.data)
        if (event.colDef.colId !== "Preview") {
            setIsCreateCampaignDialogOpen(true)
        }

    }, [setCampaignItem, setIsCreateCampaignDialogOpen, applications, workflows]);

    useEffect(() => {
        if (gridApi) {
            gridApi.setQuickFilter(campaignSearchValue);
        }
    }, [archiveSwitch, campaignSearchValue, gridApi]);

    let currentDate = new Date().toJSON().slice(0, 10);
   const downloadCSVParams = {
    fileName: 'Campaigns_' + currentDate,
    allColumns: true,
    processCellCallback: function ({column, value}: any) {
        if (column.colId === 'isActive') {
            return value === true ? "Active" : "Inactive"
        } else if (column.colId === 'createdOn') {
            return moment(
                value,
                'dddd, MMMM DD, YYYY at h:mm:ss A Z'
            ).format('MM/DD/YYYY')
        } else {
            return value;
        }
    }
};

const excelStyles: ExcelStyle[] = useMemo(() => {
    return [
        {
            id: 'header',
            font: {
                bold: true,
                size: 15
            },
            interior: {
                color: '#bbb4bb',
                pattern: 'Solid',
            },
        },
    ];
}, []);

    return (
        <>
        <CampaginListHeader gridRef={gridRef} />
        {isCampaignsFetching ? (<AdminTableBodySkeleton/>)
            : (
            <StyledComponents.StyledAdminTableContainer>
            <StyledComponents.StyledCampaignTableWrapper className="ag-theme-alpine">
                <AgGridReact
                    ref={gridRef}
                    rowData={get(listOfCampaigns, "campaigns")}
                    columnDefs={columnDefinition}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    animateRows={true} // Optional - set to 'true' to have rows animate when sorted
                    rowSelection="single" // Options - allows click selection of rows
                    excludeHiddenColumnsFromQuickFilter={true}
                    suppressDragLeaveHidesColumns={true}
                    onCellClicked={cellClickedListener} // Optional - registering for Grid Event
                    onFirstDataRendered={onFirstDataRendered}
                    defaultCsvExportParams={downloadCSVParams}
                    defaultExcelExportParams={downloadCSVParams}
                    excelStyles={excelStyles}
                />
            </StyledComponents.StyledCampaignTableWrapper></StyledComponents.StyledAdminTableContainer>)}
    </>)
}